// FeaturesContext.js
// Provides the current user's role features as a React context.
// Usage: const { hasFeature } = useFeatures();
//        hasFeature('ALLOW_PATIO_DOORS') → true/false
//
// Note: hasFeature() returning true means the role has the feature assigned and,
// for products, the user's dealer is allowed to sell it (Dealers → Products).
// It does NOT override code-level guards (e.g. PATIO_DOORS_ENABLED in constants.js).
// Both conditions must be satisfied for a feature to be active.

import React, { createContext, useContext, useEffect, useState } from 'react';
import { authFetch } from './api';
import { getCookie } from './cookies';

const FeaturesContext = createContext({ features: [], hasFeature: () => false });

export const FeaturesProvider = ({ children }) => {
  const [features, setFeatures] = useState([]);

  useEffect(() => {
    const roleName = getCookie('user_role');
    if (!roleName) return;

    // Role-only lookup (no dealer restriction); used if /my-features isn't available.
    const fetchRoleFeatures = () =>
      authFetch('/user-roles/')
        .then(roles => {
          const role = roles?.find(r => r.name === roleName);
          if (!role) return [];
          return authFetch(`/user-roles/${role.id}/features`);
        });

    // Effective features: role features minus products the user's dealer may not sell.
    // (authFetch doesn't throw on 404, so a non-list answer also means "not available".)
    authFetch('/user-roles/my-features')
      .then(data => (Array.isArray(data) ? data : fetchRoleFeatures()))
      .catch(() => fetchRoleFeatures())
      .then(data => {
        if (!data) return;
        if (Array.isArray(data)) {
          setFeatures(data);
        } else if (data?.features) {
          setFeatures(data.features);
        }
      })
      .catch(err => {
        console.warn('FeaturesContext: failed to load role features, defaulting to none:', err.message);
        setFeatures([]);
      });
  }, []);

  const hasFeature = (code) => features.includes(code);

  return (
    <FeaturesContext.Provider value={{ features, hasFeature }}>
      {children}
    </FeaturesContext.Provider>
  );
};

export const useFeatures = () => useContext(FeaturesContext);
